import { useTranslation } from 'react-i18next';
import { RoadMark } from '@/components/brand/RoadMark';
import { cn } from '@/lib/utils';

/**
 * The lockup: the road mark plus `CoreWay` in the surface's own ink and `DispatchOS` in the accent.
 *
 * TYPEFACE. The mockup sets the wordmark in a rounded geometric sans, which is NOT Inter — Inter is
 * the one family in design/tokens.json and the one the driver app also loads. This renders the
 * wordmark as live text in the token family rather than adding a second webfont for two words, or
 * freezing the letterforms into SVG paths that no locale and no screen reader can touch. The
 * difference from the mockup is real and deliberate; it is reported, not hidden.
 *
 * STACKED. `CoreWay DispatchOS` does not fit on one line inside --size-rail-w (248px), and the mockup
 * stacks it too. The footer lockup is small enough to sit inline.
 */
export function Wordmark({
  tone = 'rail',
  size = 'lg',
  layout = 'stacked',
  className,
}: {
  tone?: 'rail' | 'canvas';
  size?: 'sm' | 'lg';
  layout?: 'stacked' | 'inline';
  className?: string;
}) {
  const { t } = useTranslation();
  const onRail = tone === 'rail';
  const large = size === 'lg';

  return (
    <div className={cn('flex items-center', large ? 'gap-3' : 'gap-2', className)}>
      <RoadMark
        className={cn(large ? 'size-11' : 'size-6', 'text-accent-base')}
        laneClassName={onRail ? 'fill-on-rail' : 'fill-canvas'}
      />
      <span
        className={cn(
          'min-w-0 font-bold tracking-tight',
          large ? 'text-2xl leading-tight' : 'text-base',
          layout === 'inline' && 'flex items-baseline gap-1.5',
        )}
      >
        <span
          className={cn(
            layout === 'stacked' && 'block',
            'truncate',
            onRail ? 'text-on-rail' : 'text-ink',
          )}
        >
          {t('brand.name')}
        </span>
        <span className={cn(layout === 'stacked' && 'block', 'truncate text-accent-base')}>
          {t('brand.product')}
        </span>
      </span>
    </div>
  );
}
